import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import { CapacityEditor } from "../components/CapacityEditor";
import { AVAIL_OPTIONS } from "../lib/capacity";
import { API } from "../lib/api";

export default function AvailabilityManage() {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("cid");
  const t = searchParams.get("t");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [clinician, setClinician] = useState(null);
  const [capacityDates, setCapacityDates] = useState([]);
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!cid || !t) {
      setErrorMsg("This link is missing some information. Please use the link from your email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API}/availability/manage?cid=${encodeURIComponent(cid)}&t=${encodeURIComponent(t)}`
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || "This link is invalid or has expired.");
        }
        const data = await res.json();
        setClinician(data.clinician);
        setCapacityDates(data.capacity_dates || []);
        setLocked(!!data.locked);
      } catch (e) {
        setErrorMsg(e.message || "Something went wrong loading your availability.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, t]);

  const updateStatus = async (value) => {
    if (locked || saving || !clinician || value === clinician.availability_status) return;
    setSaving(value);
    try {
      const res = await fetch(`${API}/availability/manage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cid, t, availability_status: value }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(result.detail || "Could not update your availability.");
        return;
      }
      setClinician((prev) => ({ ...prev, availability_status: result.availability_status }));
      toast.success("Availability updated.");
    } catch (e) {
      toast.error("Could not update your availability.");
    } finally {
      setSaving(null);
    }
  };

  const handleCapacityUpdated = async (patch) => {
    if (!clinician) return;
    try {
      const res = await fetch(`${API}/availability/manage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cid,
          t,
          has_capacity_patch: true,
          capacity_count: patch.capacity_count,
          capacity_timing_type: patch.capacity_timing_type,
          capacity_from_date: patch.capacity_from_date,
          capacity_last_confirmed_at: patch.capacity_last_confirmed_at,
          capacity_expires_at: patch.capacity_expires_at,
          psychiatry_referral_type: patch.psychiatry_referral_type,
          psychiatry_referral_type_other: patch.psychiatry_referral_type_other,
          capacity_dates: patch._demoStaggerRows || [],
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(result.detail || "Could not save your capacity.");
        return;
      }
      setClinician((prev) => ({
        ...prev,
        capacity_count: result.capacity_count,
        capacity_timing_type: result.capacity_timing_type,
        capacity_from_date: result.capacity_from_date,
        capacity_expires_at: result.capacity_expires_at,
        capacity_last_confirmed_at: result.capacity_last_confirmed_at,
      }));
      setCapacityDates(result.capacity_dates || []);
      toast.success("Capacity details saved.");
    } catch (e) {
      toast.error("Could not save your capacity.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-6 text-center">
          <div className="text-[#0B1560] font-bold text-lg mb-3">SimplyRefer</div>
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-600 text-sm">{errorMsg}</p>
        </div>
      </div>
    );
  }

  const fullName =
    [clinician?.first_name, clinician?.last_name].filter(Boolean).join(" ") || clinician?.display_name;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0B1560] px-6 py-5">
        <div className="max-w-2xl mx-auto">
          <span className="text-white font-bold text-lg tracking-tight">SimplyRefer</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-heading text-lg font-semibold text-slate-900">
          {fullName ? `${fullName}'s availability` : "Your availability"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Let referring GPs know if you're accepting patients. Changes are shown straight away.
        </p>

        {locked && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              Your availability is shown as-is for now and can't be changed from this link yet.
            </p>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {AVAIL_OPTIONS.map((opt) => {
            const active = clinician?.availability_status === opt.value;
            const isSaving = saving === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateStatus(opt.value)}
                disabled={!!saving || locked}
                data-testid={`availability-set-${opt.value}`}
                style={active ? opt.style : undefined}
                className={`relative flex flex-col rounded-md border p-4 text-left transition-all duration-200 ${
                  locked ? "cursor-not-allowed opacity-60" : ""
                } ${active ? "ring-2 ring-primary/30" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">{opt.label}</span>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  ) : active ? (
                    <span
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"
                      data-testid={`availability-active-${opt.value}`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                  ) : null}
                </div>
                <span className="mt-1 text-xs text-slate-500">{opt.desc}</span>
              </button>
            );
          })}
        </div>

        {clinician && (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-slate-900">Fine-tune (optional)</h2>
            <p className="mt-1 mb-3 text-xs text-slate-500">
              Add more detail about your current capacity so GPs know exactly what to expect.
            </p>
            <CapacityEditor
              clinician={{ ...clinician, id: cid }}
              onUpdated={handleCapacityUpdated}
              locked={locked}
              demoMode={true}
              dates={capacityDates}
            />
          </div>
        )}
      </div>
    </div>
  );
}
